import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useLocation, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { getUserAvatarUrl, handleUserAvatarError } from '../utils/avatars';
import { pluralize, pluralizeCs } from '../utils/pluralize';
import CountryDisplay from './CountryDisplay';
import MedalIcon from './MedalIcon';
import PowerCurveChart from './PowerCurveChart';
import './RiderProfile.css';
import { localeFor } from '../utils/timeFormat';

// Карточка гонщика: карьера, зачеты по событиям, личные рекорды на маршрутах,
// кривая мощности и динамика веса. Страница открыта по ссылке без регистрации,
// поэтому веса и FTP у гостя в ответе просто нет — см. RiderProfileService.

const formatTime = (seconds) => {
  if (seconds === null || seconds === undefined) return '—';

  const total = Math.round(Number(seconds));
  if (!Number.isFinite(total)) return '—';

  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const rest = total % 60;

  return hours > 0
    ? `${hours}:${String(minutes).padStart(2, '0')}:${String(rest).padStart(2, '0')}`
    : `${minutes}:${String(rest).padStart(2, '0')}`;
};

const formatDate = (value, language) => {
  if (!value) return '';

  const time = Date.parse(value);
  if (Number.isNaN(time)) return '';

  return new Date(time).toLocaleDateString(localeFor(language), {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

// Возраст с подписью: голое число рядом с категорией читается как что угодно
const formatAge = (age, language) => {
  if (language === 'ru') return `${age} ${pluralize(age, 'год', 'года', 'лет')}`;
  if (language === 'it') return `${age} anni`;
  if (language === 'de') return `${age} Jahre`;
  if (language === 'es') return `${age} años`;
  if (language === 'fr') return `${age} ans`;
  if (language === 'cs') return `${age} ${pluralizeCs(age, 'rok', 'roky', 'let')}`;
  return `${age} y.o.`;
};

const StatTile = ({ label, value, unit }) => (
  <div className="rider-profile-tile">
    <div className="rider-profile-tile-value">
      {value ?? '—'}
      {value != null && unit && <span className="rider-profile-tile-unit">{unit}</span>}
    </div>
    <div className="rider-profile-tile-label">{label}</div>
  </div>
);

const RiderProfile = () => {
  const { id } = useParams();
  const { t, language } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();

  // Откуда пришли: список гонщиков кладет в state точку возврата вместе со
  // своей страницей и фильтрами. Прямая ссылка такого state не имеет — тогда
  // отматываем историю назад, а если истории нет (открыли ссылку в новой
  // вкладке), уводим на список.
  const backTo = location.state?.backTo;

  const handleBack = () => {
    if (backTo?.pathname) {
      navigate(backTo.pathname, { state: backTo.state });
    } else if (location.key !== 'default') {
      navigate(-1);
    } else {
      navigate('/users');
    }
  };

  const backLabel = t(backTo?.labelKey || 'common.back');

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.innerWidth <= 768
  );

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const fetchProfile = async () => {
      setLoading(true);
      setError('');

      try {
        const response = await axios.get(`/api/riders/${encodeURIComponent(id)}`);
        if (!cancelled) setData(response.data);
      } catch (err) {
        if (!cancelled) setError(err.response?.status === 404 ? 'not_found' : 'load_error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchProfile();
    return () => { cancelled = true; };
  }, [id]);

  const riderName = useCallback((rider) => {
    if (!rider) return '';

    return language !== 'ru' && rider.first_name_eng && rider.last_name_eng
      ? `${rider.first_name_eng} ${rider.last_name_eng}`
      : `${rider.first_name} ${rider.last_name}`;
  }, [language]);

  const career = data?.career;

  // Место: у призеров медаль (номер нарисован внутри нее), у остальных число
  const renderPlace = (position) => (
    <span className="rider-profile-position">
      {position <= 3 ? <MedalIcon place={position} /> : position}
    </span>
  );

  const tiles = useMemo(() => {
    if (!career) return [];

    return [
      { label: t('rider.starts'), value: career.starts },
      { label: t('rider.finishes'), value: career.finishes },
      { label: t('rider.wins'), value: career.wins },
      { label: t('rider.podiums'), value: career.podiums },
      { label: t('rider.best_position'), value: career.best_position },
      { label: t('rider.avg_position'), value: career.avg_position },
      { label: t('rider.distance'), value: career.distance_km, unit: t('rider.unit_km') },
      { label: t('rider.elevation'), value: career.elevation_m, unit: t('rider.unit_m') },
      { label: t('rider.hours'), value: career.hours },
      { label: t('rider.dns_dnf'), value: `${career.dns ?? 0} / ${career.dnf ?? 0}` }
    ];
  }, [career, t]);

  if (loading) {
    return <div className="rider-profile-message">{t('common.loading')}</div>;
  }

  if (error) {
    return (
      <div className="rider-profile-message">
        {error === 'not_found' ? t('rider.not_found') : t('rider.load_error')}
      </div>
    );
  }

  const { rider, events, records, power_curve: powerCurve, recent_rides: recentRides } = data;

  return (
    <div className="rider-profile">
      <button type="button" className="rider-profile-back" onClick={handleBack}>
        ← {backLabel}
      </button>

      <div className="rider-profile-header">
        <img
          className="rider-profile-avatar"
          src={getUserAvatarUrl(rider.avatar)}
          alt={riderName(rider)}
          onError={handleUserAvatarError}
        />

        <div className="rider-profile-identity">
          <h1>{riderName(rider)}</h1>
          <div className="rider-profile-nickname">@{rider.nickname}</div>

          <div className="rider-profile-meta">
            {rider.country && <CountryDisplay country={rider.country} language={language} />}
            {rider.team && <span className="rider-profile-team">{rider.team.name}</span>}
            {rider.power_category && (
              <span className="rider-profile-category">
                {rider.power_category}
                {rider.power_group ? ` · ${rider.power_group} ${t('rider.unit_wkg')}` : ''}
              </span>
            )}
            {rider.age && <span className="rider-profile-age">{formatAge(rider.age, language)}</span>}
          </div>

          {(rider.ftp || rider.weight) && (
            <div className="rider-profile-personal">
              {rider.ftp && <span>FTP {rider.ftp} {t('rider.unit_watts')}</span>}
              {rider.weight && <span>{rider.weight} {t('rider.unit_kg')}</span>}
            </div>
          )}
        </div>
      </div>

      <section className="rider-profile-section">
        <h2>{t('rider.career')}</h2>
        <div className="rider-profile-tiles">
          {tiles.map(tile => <StatTile key={tile.label} {...tile} />)}
        </div>
        {career?.first_race_at && (
          <div className="rider-profile-note">
            {t('rider.career_span')
              .replace('{from}', formatDate(career.first_race_at, language))
              .replace('{to}', formatDate(career.last_race_at, language))}
          </div>
        )}
      </section>

      {powerCurve && (
        <section className="rider-profile-section">
          <h2>{t('rider.power_curve')}</h2>
          <PowerCurveChart
            best={powerCurve.best}
            recent={powerCurve.recent}
            recentDays={powerCurve.recent_days}
          />
          <div className="rider-profile-note">
            {t('rider.power_curve_note').replace('{rides}', powerCurve.rides)}
          </div>
        </section>
      )}

      {events?.length > 0 && (
        <section className="rider-profile-section">
          <h2>{t('rider.events')}</h2>

          {isMobile ? (
            <div className="rider-profile-cards">
              {events.map(item => (
                <div key={item.event_id} className="rider-profile-card">
                  <div className="rider-profile-card-head">
                    {renderPlace(item.position)}
                    <div className="rider-profile-card-title">
                      <Link to={`/events/${item.event_id}`}>{item.name}</Link>
                      <div className="rider-profile-subtle">
                        {formatDate(item.started_at, language)} · {t('rider.of')} {item.riders}
                      </div>
                    </div>
                  </div>

                  <dl className="rider-profile-card-stats">
                    <div>
                      <dt>{t('rider.stages')}</dt>
                      <dd>{item.stages_completed}{item.stages_total ? ` / ${item.stages_total}` : ''}</dd>
                    </div>
                    <div>
                      <dt>{t('rider.total_time')}</dt>
                      <dd>{formatTime(item.total_time)}</dd>
                    </div>
                    <div>
                      <dt>{t('rider.points')}</dt>
                      <dd>{item.points}</dd>
                    </div>
                    <div>
                      <dt>{t('rider.wins_podiums')}</dt>
                      <dd>{item.wins} / {item.podiums}</dd>
                    </div>
                  </dl>
                </div>
              ))}
            </div>
          ) : (
            <div className="rider-profile-table-wrapper">
              <table className="rider-profile-table">
                <thead>
                  <tr>
                    <th>{t('rider.event')}</th>
                    <th>{t('rider.position')}</th>
                    <th>{t('rider.stages')}</th>
                    <th>{t('rider.total_time')}</th>
                    <th>{t('rider.points')}</th>
                    <th>{t('rider.wins_podiums')}</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map(item => (
                    <tr key={item.event_id}>
                      <td>
                        <Link to={`/events/${item.event_id}`}>{item.name}</Link>
                        <div className="rider-profile-subtle">{formatDate(item.started_at, language)}</div>
                      </td>
                      <td>
                        {renderPlace(item.position)}
                        <div className="rider-profile-subtle">{t('rider.of')} {item.riders}</div>
                      </td>
                      <td>{item.stages_completed}{item.stages_total ? ` / ${item.stages_total}` : ''}</td>
                      <td>{formatTime(item.total_time)}</td>
                      <td>{item.points}</td>
                      <td>{item.wins} / {item.podiums}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {records?.length > 0 && (
        <section className="rider-profile-section">
          <h2>{t('rider.records')}</h2>
          <div className="rider-profile-note">{t('rider.records_note')}</div>

          {isMobile ? (
            <div className="rider-profile-cards">
              {records.map(record => (
                <div key={`${record.route_id}-${record.event_id}`} className="rider-profile-card">
                  <div className="rider-profile-card-title">
                    {record.route_name}
                    <div className="rider-profile-subtle">
                      {record.event_name}
                      {record.raced_at ? ` · ${formatDate(record.raced_at, language)}` : ''}
                    </div>
                  </div>

                  <dl className="rider-profile-card-stats">
                    <div>
                      <dt>{t('rider.best_time')}</dt>
                      <dd className="rider-profile-strong">{formatTime(record.finish_time)}</dd>
                    </div>
                    <div>
                      <dt>{t('rider.position')}</dt>
                      <dd>{record.position ?? '—'}</dd>
                    </div>
                    <div>
                      <dt>{t('rider.attempts')}</dt>
                      <dd>{record.attempts}</dd>
                    </div>
                  </dl>
                </div>
              ))}
            </div>
          ) : (
            <div className="rider-profile-table-wrapper">
              <table className="rider-profile-table">
                <thead>
                  <tr>
                    <th>{t('rider.route')}</th>
                    <th>{t('rider.best_time')}</th>
                    <th>{t('rider.position')}</th>
                    <th>{t('rider.attempts')}</th>
                  </tr>
                </thead>
                <tbody>
                  {records.map(record => (
                    <tr key={`${record.route_id}-${record.event_id}`}>
                      <td>
                        {record.route_name}
                        <div className="rider-profile-subtle">
                          {record.event_name}
                          {record.raced_at ? ` · ${formatDate(record.raced_at, language)}` : ''}
                        </div>
                      </td>
                      <td className="rider-profile-strong">{formatTime(record.finish_time)}</td>
                      <td>{record.position ?? '—'}</td>
                      <td>{record.attempts}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {recentRides?.length > 0 && (
        <section className="rider-profile-section">
          <h2>{t('rider.recent_rides')}</h2>
          <ul className="rider-profile-rides">
            {recentRides.map(ride => (
              <li key={ride.id}>
                <div className="rider-profile-ride-name">
                  {ride.route_name || ride.title}
                  <span className="rider-profile-subtle">{formatDate(ride.date, language)}</span>
                </div>
                <div className="rider-profile-ride-stats">
                  {ride.distance_km != null && <span>{ride.distance_km} {t('rider.unit_km')}</span>}
                  {ride.elevation_m != null && <span>{ride.elevation_m} {t('rider.unit_m')}</span>}
                  {ride.moving_time != null && <span>{formatTime(ride.moving_time)}</span>}
                  {ride.normalized_power != null && <span>{ride.normalized_power} {t('rider.unit_watts')}</span>}
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
};

export default RiderProfile;
